import { UIChart } from '../chart/ui-chart';
import { Component } from '../../../shared-components/decorators/component.decorator';
import type { AxisLabelsFormatterContextObject, Options } from 'highcharts';
import type { ExtendedPoint } from '../charts.types';
import styles from './ui-chart-solidgauge.css';
import { runInNextFrame } from '../../../shared-components/global/helpers';

/** @internal */
interface SolidGaugeAnimationStepTween {
    r: number;
    end: number;
    chart?: unknown;
}

/** @internal */
interface SolidGaugeDataLabelContext {
    point: {
        y?: number | null;
        shapeArgs?: { r?: number; end?: number };
    };
}

/**
 * @class
 * @memberof ChartsComponents
 * @element ui-chart-solidgauge
 * @augments {UIChart}
 * @alias UIChartSolidGauge
 * @classdesc Represents a class for <code>ui-chart-solidgauge</code> element.
 * Draws solid gauge type chart.
 * @slot {@type "ui-series"}
 * @example
 * <ui-chart-solidgauge units="EUR" aria-label="Saving goal chart description">
 *  <ui-chart-options>
 *    {
 *      "title": { "text": "Saving goal" },
 *      "xAxis": { "categories": ["Start of month", "End of month"] },
 *      "yAxis": { "min": 0, "max": 500 }
 *    }
 *  </ui-chart-options>
 *  <ui-series>
 *     <ui-point name="Percent" y="67" color="highcharts-color-2"></ui-point>
 *  </ui-series>
 * </ui-chart-solidgauge>
 */
@Component({ tag: 'ui-chart-solidgauge', styles })
export class UIChartSolidGauge extends UIChart {
    protected min: number = 0;
    protected max: number = 100;

    override get type(): string {
        return 'solidgauge';
    }

    /**
     * Sets position on animation event.
     * Called with `this` = Highcharts tween object, `currentChart` = UIChartSolidGauge instance.
     * @param {UIChartSolidGauge} currentChart
     * @private
     */
    setAnimationStep(
        this: SolidGaugeAnimationStepTween,
        currentChart: UIChartSolidGauge
    ): void {
        runInNextFrame(() => {
            if (!currentChart.chart || !this.end) {
                return;
            }
            const point0: ExtendedPoint =
                currentChart.chart.series[0]?.points[0];
            if (point0?.dataLabel) {
                const dy = UIChart.remap(
                    this.end,
                    -Math.PI,
                    0,
                    currentChart.min,
                    currentChart.max
                );
                const formatter = point0.dataLabel.options?.formatter;
                const label = (
                    formatter as unknown as (
                        this: SolidGaugeDataLabelContext
                    ) => string
                )?.call({
                    point: {
                        y: Math.round(dy),
                        shapeArgs: {
                            r: this.r,
                            end: this.end,
                        },
                    },
                });
                point0.dataLabel.attr({
                    text: label,
                });
            }
        });
    }

    /**
     * @inheritDoc
     */
    override renderChart(): void {
        this.mergeOptionsFromElement();
        const yAxis = this.opts?.yAxis;
        if (yAxis && !Array.isArray(yAxis)) {
            this.min = Number(yAxis.min ?? 0);
            this.max = Number(yAxis.max ?? 100);
        } else {
            this.min = 0;
            this.max = 100;
        }

        // Highcharts v12+ library no longer maps CSS classes like '.highcharts-color-3' to SVG elements
        // for solid gauge points, so we must extract the color from CSS and set it as the
        // color property in JS. Hidden temporary element is created to read the computed CSS
        // fill, then that color is set explicitly on the point.
        const uiPointElement = this.querySelector('ui-series ui-point');
        let solidGaugeColor: string | undefined;
        if (uiPointElement) {
            const colorAttr =
                uiPointElement.getAttribute('color') ??
                uiPointElement.getAttribute('class') ??
                '';
            const tempDiv = document.createElement('div');
            tempDiv.style.display = 'none';
            tempDiv.className = colorAttr;
            document.body.appendChild(tempDiv);
            const computedColor = getComputedStyle(tempDiv).fill;
            document.body.removeChild(tempDiv);
            solidGaugeColor = computedColor;
        }

        const units = this.units;
        const setAnimationStep = UIChartSolidGauge.prototype.setAnimationStep;
        const currentChart = this.instance;
        const shouldDisableAnimation =
            this.opts?.plotOptions?.series?.animation === false;

        const params: Options = {
            subtitle: {
                align: 'center',
                y: 12,
            },
            pane: {
                size: '100%',
                center: ['50%', '85%'],
                startAngle: -90,
                endAngle: 90,
                background: [
                    {
                        innerRadius: '75%',
                        outerRadius: '100%',
                        shape: 'arc',
                    },
                ],
            },
            xAxis: {
                visible: true,
            },
            yAxis: {
                visible: true,
                tickPositions: [this.min, this.max],
                allowDecimals: true,
                labels: {
                    y: 16,
                    useHTML: true,
                    distance: -11,
                    formatter: function (
                        this: AxisLabelsFormatterContextObject
                    ): string {
                        let startLabel = '';
                        let endLabel = '';
                        if (this.chart.xAxis[0].categories) {
                            startLabel = this.chart.xAxis[0].categories[0];
                            endLabel = this.chart.xAxis[0].categories[1];
                        }
                        return (
                            '<div class="highcharts-data-label solidgauge-yaxis-numeric-label">' +
                            this.value +
                            '' +
                            units +
                            '</div>' +
                            '<div class="solidgauge-yaxis-label">' +
                            (this.isFirst ? startLabel : '') +
                            (this.isLast ? endLabel : '') +
                            '</div>'
                        );
                    },
                },
            },
            plotOptions: {
                solidgauge: {
                    innerRadius: '75%',
                    animation: shouldDisableAnimation
                        ? false
                        : {
                              step: function (
                                  this: SolidGaugeAnimationStepTween
                              ): void {
                                  setAnimationStep.call(
                                      this,
                                      currentChart as UIChartSolidGauge
                                  );
                              },
                          },
                    dataLabels: {
                        useHTML: true,
                        format: '{point.y}' + this.units,
                        formatter: function (this: unknown): string {
                            const ctx = this as SolidGaugeDataLabelContext;
                            const shapeArgs = ctx.point?.shapeArgs;
                            const { x, y } = UIChart.calculateCircleCoords(
                                shapeArgs?.r ?? 0,
                                shapeArgs?.end ?? 0,
                                0.2,
                                0.2
                            );
                            return (
                                '<span style="position:absolute;' +
                                'top:' +
                                Math.round(y) +
                                'px;' +
                                'left:' +
                                Math.round(x) +
                                'px;">' +
                                (ctx.point?.y ?? '') +
                                units +
                                '</span>'
                            );
                        },
                    },
                },
            },
            series: [
                {
                    type: 'solidgauge',
                    data: [
                        (() => {
                            const description =
                                this.getAttribute('aria-label') ??
                                this.opts.title?.text;
                            return {
                                y: uiPointElement
                                    ? Number(uiPointElement.getAttribute('y'))
                                    : 0,
                                ...(solidGaugeColor
                                    ? { color: solidGaugeColor }
                                    : {}),
                                ...(description
                                    ? {
                                          accessibility: {
                                              description,
                                          },
                                      }
                                    : {}),
                            };
                        })(),
                    ],
                },
            ],
            responsive: {
                rules: [
                    {
                        condition: {
                            maxWidth: 500,
                        },
                        chartOptions: {
                            chart: {
                                height: 300,
                            },
                            pane: {
                                center: ['50%', '50%'],
                            },
                            plotOptions: {
                                series: {
                                    dataLabels: {
                                        formatter: () => void 0,
                                        y: -30,
                                    },
                                },
                            },
                        },
                    },
                ],
            },
        };
        this.draw(params);
    }
}
